import { useCallback, useEffect, useMemo, useState } from 'react'
import { GraphQL } from '@guestgoodie/graphql'

import { QueryVariables } from './usePaginatedPromotions'
import debounce from 'lodash.debounce'

export default function usePromotionTable(possibleTypes: GraphQL.GoodieType[]) {
  const [selectedPromotions, setSelectedPromotions] = useState<Set<number>>(
    new Set()
  )
  const [search, setSearch] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<GraphQL.SortDirection>(
    GraphQL.SortDirection.Asc
  )
  const [sortField, setSortField] = useState<GraphQL.PromotionsSortField>(
    GraphQL.PromotionsSortField.Title
  )
  const [types, setTypes] = useState<string[]>([])

  const updateSort = useCallback(
    (field: GraphQL.PromotionsSortField, direction: GraphQL.SortDirection) => {
      setSortField(field)
      setSortDirection(direction)
    },
    [setSortField, setSortDirection]
  )

  const filterTypes = useMemo(() => {
    return (types.length === 0 ? possibleTypes : types) as
      | GraphQL.GoodieType[]
      | null
  }, [types, possibleTypes])

  const [variables, setVariables] = useState<QueryVariables>(() => {
    return {
      filter: {
        goodieTypes: filterTypes,
        search
      },
      sort: {
        direction: sortDirection,
        field: sortField
      }
    }
  })

  const onSelectPromotion = useCallback(
    (promotion: GraphQL.PromotionFragmentFragment) => {
      setSelectedPromotions(ids => {
        const newSet = new Set(ids)

        if (!newSet.delete(promotion.id)) newSet.add(promotion.id)

        return newSet
      })
    },
    [setSelectedPromotions]
  )

  const debounceSearch = useMemo(() => {
    return debounce(
      (search: string) =>
        setVariables(vars => ({
          ...vars,
          filter: {
            ...vars.filter,
            search
          }
        })),
      1000
    )
  }, [setVariables])

  useEffect(() => {
    if (variables.filter.search === search) return

    debounceSearch(search)
  }, [variables, search, debounceSearch])

  useEffect(() => {
    const currentTypes = variables.filter.goodieTypes

    const hasSameStates =
      filterTypes === currentTypes ||
      (filterTypes?.length === currentTypes?.length &&
        filterTypes?.join(',') === currentTypes?.join(','))

    if (hasSameStates) return

    setVariables(variables => ({
      ...variables,
      filter: {
        ...variables.filter,
        goodieTypes: filterTypes
      }
    }))
  }, [variables, filterTypes])

  useEffect(() => {
    if (
      variables.sort.direction === sortDirection &&
      variables.sort.field === sortField
    ) {
      return
    }

    setVariables(variables => ({
      ...variables,
      sort: {
        direction: sortDirection,
        field: sortField
      }
    }))
  }, [sortDirection, sortField, variables, setVariables])

  return {
    queryVariables: variables,
    selectedPromotions,
    search,
    types,
    onSelectPromotion,
    setTypes,
    setSearch,
    updateSort
  }
}
